import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useParams, useLocation, useHistory } from 'react-router-dom'
import axios from 'axios'
import { get } from 'lodash'

import { requestCreator } from '../utilities/requests'
import FacilitiesInactive from '../components/FacilitiesInactive/FacilitiesInactive'
import NotFoundPage from '../components/ErrorPages/NotFoundPage'

export const FacilityContext = React.createContext()

const { get: getFacility, cancel: cancelFacilityRequest } = requestCreator()

function throwRouteError() {
  throw new Error('FacilityProvider must be in a route that includes a facility_id param')
}

export default function FacilityProvider({ children, ...rest }) {
  const isMountedRef = useRef(true)
  const { facility_id: facilityId } = useParams()
  const { state, pathname } = useLocation()
  const { replace: redirect } = useHistory()
  const isEditRoute = pathname.endsWith('/edit')
  const [facilityState, setFacilityState] = useState({
    facility: {},
    isLoading: true,
    error: null
  })

  const update = useCallback((facility) => {
    if (isMountedRef.current) {
      setFacilityState((state) => ({ ...state, facility }))
    }
  }, [])

  const facilityValues = useMemo(() => {
    return { ...facilityState, update }
  }, [facilityState, update])

  if (!facilityId) throwRouteError()

  useEffect(() => {
    isMountedRef.current = true

    async function getFacilityData() {
      try {
        if (isMountedRef.current) {
          setFacilityState((state) => ({ ...state, isLoading: true, error: null }))
        }
        const facility = await getFacility(`/api/facilities/${facilityId}`, {
          params: { include: 'curriculums,billing_contact,address' }
        })
        if (isMountedRef.current) {
          setFacilityState({ facility, isLoading: false, error: null })
        }
      } catch (error) {
        if (axios.isCancel(error)) return

        if (isMountedRef.current) {
          const status = get(error, 'response.status')
          if (status === 404 || status === 422) {
            setFacilityState({ facility: {}, isLoading: false, error: 'not_found' })
          } else {
            setFacilityState((state) => ({ ...state, isLoading: false }))
            redirect('/500')
          }
        }
      }
    }

    getFacilityData()

    return () => {
      isMountedRef.current = false
      cancelFacilityRequest()
    }
  }, [facilityId, redirect])

  if (facilityState.error === 'not_found') {
    return <NotFoundPage />
  }

  const shouldShowInactive = get(state, 'isGated') && !isEditRoute

  return (
    <FacilityContext.Provider value={facilityValues} {...rest}>
      {shouldShowInactive ? <FacilitiesInactive /> : children}
    </FacilityContext.Provider>
  )
}

FacilityProvider.propTypes = {
  children: PropTypes.node
}

export { FacilityProvider }
